import { useState, useCallback, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';
import { Button } from '@/core/components/ui/button';
import { Label } from '@/core/components/ui/label';
import { Input } from '@/core/components/ui/input';
import { Textarea } from '@/core/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { Alert, AlertDescription } from '@/core/components/ui/alert';
import { Upload, X, AlertCircle, Image, FileImage, Film, FileJson, FileUp, FileCode2, Loader2 } from 'lucide-react';
import { cn } from '@/core/lib/utils';
import {
  AssetApplicationType,
  AssetType,
  ScreenSize,
  SCREEN_SIZE_LABELS,
  ASSET_SIZE_LIMITS,
  ASSET_ALLOWED_EXTENSIONS,
  AssetUploadFormData,
  getApplicationTypeOptions,
  getAssetTypeOptions,
  isValidAssetFile,
  formatFileSize,
  getAcceptString,
  fileToBase64,
} from '../../types/asset-types';
import { useAuthStore } from '@/core/stores/auth-store';
import { useOperatorStore } from '@/core/stores/operator-store';

/**
 * Sanitizes filename for URL-safe S3 storage.
 * - Replaces spaces with underscores
 * - Removes characters not safe for URLs (keeps alphanumeric, dash, underscore, dot)
 * - Collapses multiple underscores/dashes into single ones
 */
function sanitizeFileName(fileName: string): string {
  // Split into name and extension
  const lastDotIndex = fileName.lastIndexOf('.');
  const name = lastDotIndex > 0 ? fileName.slice(0, lastDotIndex) : fileName;
  const extension = lastDotIndex > 0 ? fileName.slice(lastDotIndex) : '';

  // Sanitize the name part
  const sanitizedName = name
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/[^a-zA-Z0-9_\-]/g, '') // Remove non-URL-safe characters
    .replace(/[_\-]+/g, '_') // Collapse multiple underscores/dashes
    .replace(/^[_\-]+|[_\-]+$/g, ''); // Trim leading/trailing underscores/dashes

  return sanitizedName + extension.toLowerCase();
}

interface AssetUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onUpload: (request: AssetUploadFormData) => Promise<void>;
  isUploading: boolean;
  error: string | null;
  onClearError: () => void;
  /** Pre-populate operator selection from grid filter (for distributors) */
  initialOperatorId?: number | null;
  /** Pre-populate machine ID from grid filter */
  initialMachineId?: number | null;
  /** Pre-populate application type from grid filter */
  initialApplicationType?: AssetApplicationType | null;
}

function getAssetTypeIcon(assetType: AssetType) {
  switch (assetType) {
    case AssetType.Icon:
      return <FileImage className="h-8 w-8 text-blue-500" />;
    case AssetType.Image:
      return <Image className="h-8 w-8 text-green-500" />;
    case AssetType.Video:
      return <Film className="h-8 w-8 text-purple-500" />;
    case AssetType.Lottie:
      return <FileJson className="h-8 w-8 text-orange-500" />;
    case AssetType.File:
      return <FileUp className="h-8 w-8 text-gray-500" />;
    case AssetType.Xml:
      return <FileCode2 className="h-8 w-8 text-yellow-600" />;
  }
}

export function AssetUploadDialog({
  open,
  onClose,
  onUpload,
  isUploading,
  error,
  onClearError,
  initialOperatorId,
  initialMachineId,
  initialApplicationType,
}: AssetUploadDialogProps) {
  const [applicationType, setApplicationType] = useState<AssetApplicationType | null>(null);
  const [assetType, setAssetType] = useState<AssetType | null>(null);
  const [screenSize, setScreenSize] = useState<ScreenSize | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [machineId, setMachineId] = useState<number | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check if user is a distributor with multiple operators
  const isDistributor = useAuthStore((state) => state.isDistributor);
  const operators = useOperatorStore((state) => state.operators);
  const selectedOperator = useOperatorStore((state) => state.selectedOperator);
  const loadOperators = useOperatorStore((state) => state.loadOperators);
  const isLoadingOperators = useOperatorStore((state) => state.isLoading);
  const showOperatorSelector = isDistributor && operators.length > 1;

  const applicationOptions = getApplicationTypeOptions();
  const assetTypeOptions = getAssetTypeOptions();

  // Reset form when dialog opens/closes, using initial values from grid filters
  useEffect(() => {
    if (open) {
      // Pre-populate from grid filters when opening
      setApplicationType(initialApplicationType ?? null);
      setAssetType(null);
      setScreenSize(null);
      setName('');
      setDescription('');
      setMachineId(initialMachineId ?? null);
      setFile(null);
      setFilePreview(null);
      setValidationError(null);
      // For operator, we'll set it in a separate effect after operators load
    } else {
      // Clear everything when closing
      setApplicationType(null);
      setAssetType(null);
      setScreenSize(null);
      setName('');
      setDescription('');
      setMachineId(null);
      setFile(null);
      setFilePreview(null);
      setValidationError(null);
      setSelectedOperatorId(null);
      onClearError();
    }
  }, [open, initialApplicationType, initialMachineId, onClearError]);

  // Load operators when dialog opens for distributors
  useEffect(() => {
    if (open && isDistributor && operators.length === 0 && !isLoadingOperators) {
      loadOperators();
    }
  }, [open, isDistributor, operators.length, isLoadingOperators, loadOperators]);

  // Set operator when dialog opens for distributors
  // Priority: 1) initialOperatorId from grid filter, 2) currently selected operator
  useEffect(() => {
    if (open && showOperatorSelector && selectedOperatorId === null) {
      if (initialOperatorId) {
        // Use the operator from grid filter
        setSelectedOperatorId(initialOperatorId);
      } else if (selectedOperator) {
        // Fall back to currently selected operator
        setSelectedOperatorId(selectedOperator.operatorId);
      }
    }
  }, [open, showOperatorSelector, selectedOperator, selectedOperatorId, initialOperatorId]);

  // Clean up file preview URL
  useEffect(() => {
    return () => {
      if (filePreview) {
        URL.revokeObjectURL(filePreview);
      }
    };
  }, [filePreview]);

  const handleFileSelect = useCallback((selectedFile: File) => {
    if (!assetType) {
      setValidationError('Please select an asset type first');
      return;
    }

    const validation = isValidAssetFile(selectedFile, assetType);
    if (!validation.valid) {
      setValidationError(validation.error || 'Invalid file');
      return;
    }

    setValidationError(null);
    setFile(selectedFile);

    // Create preview for images
    if (assetType === AssetType.Icon || assetType === AssetType.Image) {
      const previewUrl = URL.createObjectURL(selectedFile);
      setFilePreview(previewUrl);
    }

    // Auto-fill name from filename if empty
    if (!name) {
      const baseName = selectedFile.name.replace(/\.[^/.]+$/, '');
      setName(baseName);
    }
  }, [assetType, name]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
        handleFileSelect(e.target.files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        handleFileSelect(e.dataTransfer.files[0]);
      }
    },
    [handleFileSelect]
  );

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
    if (filePreview) {
      URL.revokeObjectURL(filePreview);
      setFilePreview(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [filePreview]);

  const handleAssetTypeChange = useCallback((value: string) => {
    const newType = value as AssetType;
    setAssetType(newType);
    // Clear screen size when switching away from XML
    if (newType !== AssetType.Xml) {
      setScreenSize(null);
    }
    // Clear file if type changes
    if (file) {
      handleRemoveFile();
    }
  }, [file, handleRemoveFile]);

  const handleSubmit = useCallback(async () => {
    if (!applicationType || !assetType || !name.trim() || !file) {
      return;
    }

    // For distributors, require operator selection
    if (showOperatorSelector && !selectedOperatorId) {
      setValidationError('Please select an operator');
      return;
    }

    const base64Content = await fileToBase64(file);

    await onUpload({
      applicationType,
      assetType,
      name: name.trim(),
      description: description.trim() || undefined,
      machineId: machineId ?? undefined,
      fileName: sanitizeFileName(file.name),
      fileContent: base64Content,
      contentType: file.type || undefined,
      operatorId: showOperatorSelector ? selectedOperatorId ?? undefined : undefined,
      screenSize: screenSize ?? undefined,
    });
  }, [applicationType, assetType, name, description, machineId, file, onUpload, showOperatorSelector, selectedOperatorId]);

  const handleClose = useCallback(() => {
    if (!isUploading) {
      onClose();
    }
  }, [isUploading, onClose]);

  const canSubmit =
    applicationType &&
    assetType &&
    name.trim() &&
    file &&
    !validationError &&
    (!showOperatorSelector || selectedOperatorId) &&
    (assetType !== AssetType.Xml || screenSize !== null);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-[95vw] sm:max-w-[500px] max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Upload Asset</DialogTitle>
          <DialogDescription>
            Upload a new asset (icon, image, video, Lottie animation, or XML file).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Operator Selection (only for distributors with multiple operators) */}
          {showOperatorSelector && (
            <div className="space-y-2">
              <Label htmlFor="operator">Operator *</Label>
              <Select
                value={selectedOperatorId?.toString() || ''}
                onValueChange={(val) => setSelectedOperatorId(Number(val))}
              >
                <SelectTrigger id="operator">
                  <SelectValue placeholder="Select operator" />
                </SelectTrigger>
                <SelectContent>
                  {operators.map((op) => (
                    <SelectItem key={op.operatorId} value={op.operatorId.toString()}>
                      {op.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Application Type */}
          <div className="space-y-2">
            <Label htmlFor="applicationType">Application Type *</Label>
            <Select
              value={applicationType || ''}
              onValueChange={(val) => setApplicationType(val as AssetApplicationType)}
            >
              <SelectTrigger id="applicationType">
                <SelectValue placeholder="Select application" />
              </SelectTrigger>
              <SelectContent>
                {applicationOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Asset Type */}
          <div className="space-y-2">
            <Label htmlFor="assetType">Asset Type *</Label>
            <Select
              value={assetType || ''}
              onValueChange={handleAssetTypeChange}
            >
              <SelectTrigger id="assetType">
                <SelectValue placeholder="Select asset type" />
              </SelectTrigger>
              <SelectContent>
                {assetTypeOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label} (max {formatFileSize(ASSET_SIZE_LIMITS[opt.value])})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {assetType && (
              <p className="text-xs text-muted-foreground">
                Allowed extensions: {ASSET_ALLOWED_EXTENSIONS[assetType].join(', ')}
              </p>
            )}
          </div>

          {/* Screen Size — XML only */}
          {assetType === AssetType.Xml && (
            <div className="space-y-2">
              <Label>Screen Size *</Label>
              <div className="inline-flex rounded-full border border-gray-200 bg-gray-100 p-0.5 gap-0.5">
                {Object.values(ScreenSize).map((size) => (
                  <button
                    key={size}
                    type="button"
                    onClick={() => setScreenSize(size)}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                      screenSize === size
                        ? 'bg-yellow-400 text-gray-900 shadow-sm'
                        : 'text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {SCREEN_SIZE_LABELS[size]}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Asset name (e.g., battery_icon)"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              rows={2}
            />
          </div>

          {/* Machine ID (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="machineId">Machine ID (Optional)</Label>
            <Input
              id="machineId"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={machineId ?? ''}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setMachineId(null);
                } else {
                  // Only allow digits
                  const cleanValue = value.replace(/[^0-9]/g, '');
                  if (cleanValue) {
                    const numValue = parseInt(cleanValue, 10);
                    if (!isNaN(numValue) && numValue > 0) {
                      setMachineId(numValue);
                    }
                  }
                }
              }}
              placeholder="Assign to specific machine"
            />
            <p className="text-xs text-muted-foreground">
              Leave empty for operator-level asset, or enter Machine ID for machine-specific asset
            </p>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label>File *</Label>
            <input
              ref={fileInputRef}
              type="file"
              accept={assetType ? getAcceptString(assetType) : ''}
              onChange={handleFileChange}
              className="hidden"
              disabled={!assetType}
            />

            {file ? (
              <div className="border rounded-lg p-3 w-full">
                <div className="flex items-center gap-3 w-full">
                  {/* Preview */}
                  <div className="flex-shrink-0 h-12 w-12">
                    {filePreview ? (
                      <div className="h-12 w-12 rounded bg-gray-100 flex items-center justify-center overflow-hidden">
                        <img
                          src={filePreview}
                          alt="Preview"
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="h-12 w-12 rounded bg-gray-100 flex items-center justify-center">
                        {assetType && getAssetTypeIcon(assetType)}
                      </div>
                    )}
                  </div>
                  {/* File info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate" title={file.name}>{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                  {/* Remove button */}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleRemoveFile}
                    className="flex-shrink-0 h-8 w-8"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div
                onClick={assetType ? handleBrowseClick : undefined}
                onDragOver={assetType ? handleDragOver : undefined}
                onDragLeave={handleDragLeave}
                onDrop={assetType ? handleDrop : undefined}
                className={cn(
                  'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
                  !assetType && 'opacity-50 cursor-not-allowed',
                  assetType && !isDragging && 'border-gray-300 hover:border-gray-400 cursor-pointer',
                  isDragging && 'border-primary bg-primary/5'
                )}
              >
                <Upload className="mx-auto h-8 w-8 text-gray-400" />
                <p className="mt-2 text-sm text-gray-600">
                  {assetType ? (
                    <>
                      Drop file here or{' '}
                      <span className="text-primary font-medium">browse</span>
                    </>
                  ) : (
                    'Select asset type first'
                  )}
                </p>
                {assetType && (
                  <p className="mt-1 text-xs text-gray-500">
                    Max: {formatFileSize(ASSET_SIZE_LIMITS[assetType])}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Validation Error */}
          {validationError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}

          {/* Upload Error */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isUploading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isUploading}>
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              'Upload'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
